import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { LayoutDashboard, PawPrint, CalendarDays, Coins, Headphones, Gift, Star } from 'lucide-react';

const LOGO_COLOR = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png';

const NAV = [
  { to: '/paws', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/paws/pets', label: 'My Pets', icon: PawPrint },
  { to: '/paws/bookings', label: 'Bookings', icon: CalendarDays },
  { to: '/paws/credits', label: 'Credits', icon: Coins },
  { to: '/paws/subscriptions', label: 'Subscriptions', icon: Star },
  { to: '/paws/referral', label: 'Refer & Earn', icon: Gift },
  { to: '/paws/support', label: 'Support', icon: Headphones },
];

export default function PawsLayout() {
  const { user, setShowAuth } = useAuth();
  const location = useLocation();

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <h2 className="font-outfit text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Sign in to access your Paws account</h2>
          <button onClick={() => setShowAuth(true)} className="btn-primary" data-testid="paws-login-btn">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 flex" style={{ background: 'var(--bg-primary)' }}>
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r p-4 pt-8 shrink-0 sticky top-16 h-[calc(100vh-64px)]" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }} data-testid="paws-sidebar">
        <div className="mb-6 px-3">
          <img src={LOGO_COLOR} alt="Petfolk" className="h-8 w-auto object-contain mb-2" />
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Welcome, {user.name}</p>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map(n => {
            const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`paws-nav-${n.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active ? 'bg-petfolk-orange/10 text-petfolk-orange font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: !active ? 'var(--text-secondary)' : undefined }}
              >
                <n.icon size={18} />
                {n.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t flex justify-around py-2" style={{ borderColor: 'var(--border-color)' }} data-testid="paws-mobile-nav">
        {NAV.map(n => {
          const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-0.5 px-3 py-1 text-xs ${active ? 'text-petfolk-orange' : ''}`} style={{ color: !active ? 'var(--text-secondary)' : undefined }}>
              <n.icon size={20} />
              {n.label}
            </Link>
          );
        })}
      </div>

      {/* Content */}
      <main className="flex-1 p-4 sm:p-8 pb-20 md:pb-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
